// Browser-side voice fallback. When the LiveKit-backed Vella voice agent is
// unreachable (Railway down, missing LIVEKIT_* env vars, agent worker hasn't
// joined the room) we drop down to the Web Speech API so the user gets *some*
// voice loop instead of staring at a broken End pill.
//
// Capabilities:
//   • STT  via webkitSpeechRecognition / SpeechRecognition (Chrome, Edge, Safari).
//   • TTS  via window.speechSynthesis as the last resort, falling back to the
//     backend's /api/voice/speak (ElevenLabs) when available.
//
// This is intentionally NOT a full conversational agent — it does one
// listen → reply → speak round and stops, the user can re-tap the mic for
// another turn. We don't try to emulate barge-in or continuous duplex.

export function isBrowserSpeechSupported() {
  if (typeof window === "undefined") return false;
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  return !!SR && !!window.speechSynthesis;
}

/**
 * Run one listen → reply → speak round.
 *
 * @param {object} cfg
 * @param {(transcript: string) => Promise<string>} cfg.onAskVella - given the
 *   final transcript, returns Vella's text reply.
 * @param {(text: string) => Promise<void>} [cfg.onSpeak] - optional override
 *   that plays the reply. Defaults to window.speechSynthesis.
 * @param {(phase: "listening"|"thinking"|"speaking"|"idle"|"error", info?: object) => void} [cfg.onPhase]
 * @returns {{ stop: () => void }}
 */
export function startBrowserVoiceLoop(cfg) {
  const { onAskVella, onSpeak, onPhase } = cfg || {};
  const SR = (typeof window !== "undefined") && (window.SpeechRecognition || window.webkitSpeechRecognition);
  if (!SR) {
    onPhase?.("error", { reason: "no_speech_recognition" });
    return { stop: () => {} };
  }

  const rec = new SR();
  rec.continuous = false;
  rec.interimResults = false;
  rec.lang = "en-US";

  let stopped = false;

  rec.onstart = () => onPhase?.("listening");
  rec.onerror = (e) => {
    if (stopped) return;
    onPhase?.("error", { reason: e?.error || "speech_error" });
  };
  rec.onresult = async (event) => {
    if (stopped) return;
    const transcript = Array.from(event.results)
      .map(r => r[0]?.transcript || "")
      .join(" ")
      .trim();
    if (!transcript) {
      onPhase?.("idle");
      return;
    }
    onPhase?.("thinking", { transcript });
    let reply = "";
    try {
      reply = await onAskVella(transcript);
    } catch (err) {
      onPhase?.("error", { reason: "ask_failed", error: err });
      return;
    }
    if (stopped) return;
    onPhase?.("speaking", { transcript, reply });
    try {
      if (onSpeak) {
        await onSpeak(reply);
      } else {
        await speakViaSynthesis(reply);
      }
    } catch (err) {
      // Speaking failed — surface the text reply anyway.
      console.warn("[voiceFallback] speak failed:", err);
    }
    if (!stopped) onPhase?.("idle");
  };
  rec.onend = () => {
    // SpeechRecognition fires `onend` after `onresult`/`onerror`. We let
    // the explicit `idle` from onresult win; only emit it here if nothing
    // else has.
    if (stopped) onPhase?.("idle");
  };

  try {
    rec.start();
  } catch (err) {
    onPhase?.("error", { reason: "start_failed", error: err });
  }

  return {
    stop: () => {
      stopped = true;
      try { rec.stop(); } catch (_) {}
      try { window.speechSynthesis?.cancel(); } catch (_) {}
      onPhase?.("idle");
    },
  };
}

function speakViaSynthesis(text) {
  return new Promise((resolve) => {
    if (!text || typeof window === "undefined" || !window.speechSynthesis) {
      resolve();
      return;
    }
    try {
      const u = new SpeechSynthesisUtterance(text);
      // Try to pick a clean en-US voice when one is available.
      const voices = window.speechSynthesis.getVoices?.() || [];
      const preferred = voices.find(v => /en[-_]US/i.test(v.lang) && /female|samantha|google|natural/i.test(v.name))
        || voices.find(v => /en[-_]US/i.test(v.lang))
        || voices[0];
      if (preferred) u.voice = preferred;
      u.rate = 1.02;
      u.pitch = 1.0;
      u.onend = () => resolve();
      u.onerror = () => resolve();
      window.speechSynthesis.speak(u);
    } catch (_) {
      resolve();
    }
  });
}
